import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  FileText,
  Upload,
  Trash2,
  Download,
  Loader2,
  Plus,
  X,
  AlertTriangle,
  CheckCircle,
  Info,
  Scale,
  MapPin,
  Calendar,
  User,
  Building2,
  ClipboardList,
  FilePlus,
  Send,
  FileDown,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { num: 1, label: 'Before You File', icon: Info },
  { num: 2, label: 'Complaint Intake', icon: ClipboardList },
  { num: 3, label: 'Violation Mapping', icon: Scale },
  { num: 4, label: 'Evidence Builder', icon: FilePlus },
  { num: 5, label: 'Fee Schedule', icon: FileDown },
  { num: 6, label: 'Destination', icon: Send },
  { num: 7, label: 'Review & Generate', icon: FileText },
  { num: 8, label: 'Download', icon: Download },
];

const PROCEDURAL_OPTIONS = [
  'Lack of proper notice',
  'Improper handling of case/matter',
  'Failure to follow established protocol',
  'Unreasonable delay in response or action',
  'Bias or prejudice in process',
  'Denial of opportunity to be heard',
  'Failure to provide required documentation',
  'Improper service of process',
];
const RIGHTS_OPTIONS = [
  'Due process concerns',
  'Equal protection concerns',
  'Freedom of speech / expression',
  'Privacy rights',
  'Right to petition for redress',
  'Right to a fair and impartial tribunal',
  'Protection against unreasonable seizure',
  'Right to access public records',
];
const ADMIN_OPTIONS = [
  'Violation of internal agency policy',
  'Failure to adhere to published guidelines',
  'Improper use of authority or discretion',
  'Conflict of interest',
  'Failure to maintain required records',
  'Breach of fiduciary duty',
  'Misallocation of public resources',
  'Failure to respond to formal inquiry',
];

const RECIPIENT_TYPES = [
  'Agency Complaint Office',
  'Internal Affairs',
  'Inspector General',
  'Oversight Board',
  'Insurer',
  'Court Clerk',
  'Federal Agency',
  'State Agency',
  'Local Government Office',
  'Other',
];

export default function RedressPortal() {
  const { toast } = useToast();
  const [complaints, setComplaints] = useState([]);
  const [activeComplaint, setActiveComplaint] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Step 2: Intake
  const [intake, setIntake] = useState({
    complainant_name: '', agent_name: '', agency: '',
    incident_date: '', location: '', description: '',
    impact: '', prior_notices: '',
  });

  // Step 3: Violations
  const [violations, setViolations] = useState({
    procedural: [], rights: [], administrative: [], details: '',
  });

  // Step 4: Evidence
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Step 6: Fee Schedule
  const [feeSchedule, setFeeSchedule] = useState({ enabled: false, items: [], calculation_notes: '' });

  // Step 7: Destination
  const [destination, setDestination] = useState({ recipient_type: '', recipient_name: '', address: '' });

  // Step 8: Generated doc
  const [generatedDoc, setGeneratedDoc] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/redress/list`, { headers });
      setComplaints(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast({ title: 'Subscription Required', description: 'The Redress Portal requires a paid plan (Justice Seeker or above).', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  const loadComplaint = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/redress/${id}`, { headers });
      const c = res.data;
      setActiveComplaint(c);
      setCurrentStep(c.current_step || 1);
      setIntake(c.intake || { complainant_name: '', agent_name: '', agency: '', incident_date: '', location: '', description: '', impact: '', prior_notices: '' });
      setViolations(c.violations || { procedural: [], rights: [], administrative: [], details: '' });
      setEvidence(c.evidence || []);
      setFeeSchedule(c.fee_schedule || { enabled: false, items: [], calculation_notes: '' });
      setDestination(c.destination || { recipient_type: '', recipient_name: '', address: '' });
      setGeneratedDoc(c.generated_doc || null);
    } catch {
      toast({ title: 'Error', description: 'Failed to load complaint.', variant: 'destructive' });
    }
  };

  const createNew = async () => {
    try {
      const res = await axios.post(`${API_BASE}/redress/create`, {}, { headers });
      await fetchComplaints();
      loadComplaint(res.data.id);
    } catch (err) {
      if (err.response?.status === 403) {
        toast({ title: 'Subscription Required', description: 'Upgrade to Justice Seeker or above to access the Redress Portal.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: 'Failed to create complaint.', variant: 'destructive' });
      }
    }
  };

  const saveProgress = async (stepOverride) => {
    if (!activeComplaint) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/redress/${activeComplaint.id}`, {
        current_step: stepOverride || currentStep,
        intake, violations, fee_schedule: feeSchedule, destination,
      }, { headers });
    } catch {
      toast({ title: 'Save Failed', description: 'Could not save progress.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    await saveProgress(currentStep + 1);
    setCurrentStep(s => Math.min(s + 1, 8));
  };
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleUploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeComplaint) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', '');
      const res = await axios.post(`${API_BASE}/redress/${activeComplaint.id}/evidence`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        const updated = await axios.get(`${API_BASE}/redress/${activeComplaint.id}`, { headers });
        setEvidence(updated.data.evidence || []);
        toast({ title: 'Evidence Uploaded', description: `${file.name} added as ${res.data.evidence.exhibit_label}` });
      }
    } catch {
      toast({ title: 'Upload Failed', description: 'Could not upload evidence file.', variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteEvidence = async (evidenceId) => {
    try {
      await axios.delete(`${API_BASE}/redress/${activeComplaint.id}/evidence/${evidenceId}`, { headers });
      setEvidence(prev => prev.filter(e => e.id !== evidenceId));
      toast({ title: 'Evidence Removed' });
    } catch {
      toast({ title: 'Error', description: 'Could not delete evidence.', variant: 'destructive' });
    }
  };

  const deleteComplaint = async (id) => {
    try {
      await axios.delete(`${API_BASE}/redress/${id}`, { headers });
      if (activeComplaint?.id === id) { setActiveComplaint(null); setCurrentStep(1); }
      await fetchComplaints();
      toast({ title: 'Complaint Deleted' });
    } catch {
      toast({ title: 'Error', description: 'Could not delete complaint.', variant: 'destructive' });
    }
  };

  const generateComplaint = async () => {
    if (!activeComplaint) return;
    setGenerating(true);
    await saveProgress();
    try {
      const res = await axios.post(`${API_BASE}/redress/${activeComplaint.id}/generate`, {}, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        let attempts = 0;
        const poll = async () => {
          while (attempts < 60) {
            attempts++;
            await new Promise(r => setTimeout(r, 3000));
            try {
              const pollRes = await axios.get(`${API_BASE}/redress/${activeComplaint.id}/job/${jobId}`, { headers });
              if (pollRes.data.status === 'completed') {
                setGeneratedDoc(pollRes.data.document);
                setCurrentStep(8);
                toast({ title: 'Complaint Generated', description: 'Your formal complaint document is ready for download.' });
                return;
              } else if (pollRes.data.status === 'failed') {
                throw new Error(pollRes.data.error || 'Generation failed');
              }
            } catch (pollErr) {
              if (pollErr.message) throw pollErr;
            }
          }
          throw new Error('Generation timed out. Please try again.');
        };
        await poll();
      }
    } catch (err) {
      toast({ title: 'Generation Failed', description: err.message || 'Could not generate complaint.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const downloadDoc = () => {
    if (!generatedDoc?.content) return;
    try {
      const bytes = atob(generatedDoc.content);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generatedDoc.filename || 'Formal_Complaint.docx';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      toast({ title: 'Downloaded', description: 'Formal complaint document saved.' });
    } catch {
      toast({ title: 'Download Failed', variant: 'destructive' });
    }
  };

  const toggleViolation = (category, value) => {
    setViolations(prev => {
      const arr = prev[category] || [];
      return { ...prev, [category]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  // Safe accessors for violation arrays (guard against undefined from saved data)
  const vProcedural = violations.procedural || [];
  const vRights = violations.rights || [];
  const vAdmin = violations.administrative || [];

  // --- LIST VIEW (no active complaint) ---
  if (!activeComplaint) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="redress-portal">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-red-600" />
              Redress Portal
            </h1>
            <p className="text-sm text-gray-600 mt-1">Prepare structured complaints against alleged public agent misconduct</p>
          </div>
          <Button onClick={createNew} className="bg-red-600 hover:bg-red-700 text-white" data-testid="create-new-complaint-btn">
            <Plus className="w-4 h-4 mr-2" /> New Complaint
          </Button>
        </div>

        {/* Portal Description */}
        <Card className="border-red-100 bg-gradient-to-r from-red-50 to-white">
          <CardContent className="p-5">
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-red-600" /> What is the Redress Portal?
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              The Redress Portal is a guided tool that helps you prepare a formal, professional complaint when you believe a public agent has engaged in misconduct or violated established procedures. It walks you through collecting the facts, categorizing your concerns, attaching evidence, and generating a court-ready document — all in one place.
            </p>
            <h3 className="font-medium text-gray-800 text-sm mb-2">How it works:</h3>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gray-600">
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 1:</span> Learn about the complaint process and what to expect</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 2:</span> Enter the incident details — who, what, when, where</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 3:</span> Map concerns to procedural, rights-based, or policy categories</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 4:</span> Upload supporting evidence (auto-labeled as exhibits)</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 5:</span> Optionally add a fee or damages schedule</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 6:</span> Identify where to send the complaint</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 7:</span> Review everything and generate your formal complaint</div>
              <div className="flex items-start gap-2"><span className="font-semibold text-red-600 flex-shrink-0">Step 8:</span> Download your complaint as a professional DOCX document</div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : complaints.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Shield className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Complaints Yet</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                The Redress Portal guides you through preparing a structured, evidence-backed complaint
                related to alleged misconduct or procedural violations by a public agent.
              </p>
              <Button onClick={createNew} className="bg-red-600 hover:bg-red-700 text-white" data-testid="create-first-complaint-btn">
                <Plus className="w-4 h-4 mr-2" /> Start Your First Complaint
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {complaints.map(c => (
              <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadComplaint(c.id)} data-testid={`complaint-card-${c.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">
                        {c.intake?.agent_name ? `Complaint: ${c.intake.agent_name}` : `Complaint Draft`}
                      </h3>
                      <Badge className={c.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {c.status === 'completed' ? 'Complete' : `Step ${c.current_step || 1}/8`}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {c.intake?.agency || 'No agency specified'} — {new Date(c.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 ml-2" onClick={(e) => { e.stopPropagation(); deleteComplaint(c.id); }} data-testid={`delete-complaint-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- WIZARD VIEW ---
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4" data-testid="redress-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => { saveProgress(); setActiveComplaint(null); }} data-testid="back-to-list-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> All Complaints
        </Button>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-gray-400">Saving...</span>}
          <Badge className="bg-red-100 text-red-700">Step {currentStep} of 8</Badge>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex gap-1">
        {STEPS.map(s => (
          <div key={s.num} className={`h-1.5 flex-1 rounded-full transition-colors ${currentStep >= s.num ? 'bg-red-500' : 'bg-gray-200'}`} />
        ))}
      </div>

      {/* Step Labels (desktop) */}
      <div className="hidden sm:flex justify-between text-xs text-gray-500 -mt-1">
        {STEPS.map(s => (
          <span key={s.num} className={`${currentStep === s.num ? 'text-red-600 font-semibold' : ''}`}>
            {s.label}
          </span>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-5 sm:p-6">

          {/* STEP 1: Education */}
          {currentStep === 1 && (
            <div className="space-y-5" data-testid="step-1-education">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" /> Before You File
              </h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3 text-sm text-blue-800">
                <p className="font-semibold">What is a formal complaint against a public official?</p>
                <p>A complaint is a structured document that describes alleged misconduct or procedural violations by a public agent. It is submitted to the appropriate oversight body for review and potential action.</p>
                <p className="font-semibold mt-3">Common complaint channels include:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Internal Affairs divisions</li>
                  <li>Agency oversight or compliance offices</li>
                  <li>Inspector General offices</li>
                  <li>Oversight boards or commissions</li>
                  <li>Courts (when appropriate)</li>
                </ul>
                <p className="font-semibold mt-3">Key principles:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Factual accuracy is critical</strong> — include only what you can support with evidence</li>
                  <li><strong>Non-accusatory tone</strong> — describe concerns, do not assert legal conclusions</li>
                  <li><strong>Document everything</strong> — dates, names, locations, correspondence</li>
                  <li><strong>Check local rules</strong> — verify procedures for your jurisdiction before submitting</li>
                </ul>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Disclaimer</p>
                  <p>This portal is an educational tool designed to help you organize information and prepare a structured complaint document. It does not constitute legal advice. Incomplete or unsupported claims may weaken your position. Always verify procedures with the relevant authority before submitting.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Intake */}
          {currentStep === 2 && (
            <div className="space-y-5" data-testid="step-2-intake">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-red-600" /> Complaint Intake
              </h2>
              <p className="text-sm text-gray-600">Provide the foundational details about the incident and parties involved.</p>
              <div className="grid gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Your Full Name (Complainant)</label>
                  <Input value={intake.complainant_name} onChange={e => setIntake(p => ({ ...p, complainant_name: e.target.value }))} placeholder="Enter your full legal name" data-testid="intake-complainant-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Name of Public Agent (if known)</label>
                    <Input value={intake.agent_name} onChange={e => setIntake(p => ({ ...p, agent_name: e.target.value }))} placeholder="Agent's name" data-testid="intake-agent-name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Agency or Department</label>
                    <Input value={intake.agency} onChange={e => setIntake(p => ({ ...p, agency: e.target.value }))} placeholder="Agency name" data-testid="intake-agency" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Date(s) of Incident</label>
                    <Input value={intake.incident_date} onChange={e => setIntake(p => ({ ...p, incident_date: e.target.value }))} placeholder="e.g., March 15, 2026" data-testid="intake-date" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location</label>
                    <Input value={intake.location} onChange={e => setIntake(p => ({ ...p, location: e.target.value }))} placeholder="Address or jurisdiction" data-testid="intake-location" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Description of Conduct</label>
                  <Textarea value={intake.description} onChange={e => setIntake(p => ({ ...p, description: e.target.value }))} placeholder="Describe what the public agent did or failed to do..." rows={4} data-testid="intake-description" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">How Were You Affected?</label>
                  <Textarea value={intake.impact} onChange={e => setIntake(p => ({ ...p, impact: e.target.value }))} placeholder="Describe the impact on you..." rows={3} data-testid="intake-impact" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Prior Notices or Complaints Sent</label>
                  <Textarea value={intake.prior_notices} onChange={e => setIntake(p => ({ ...p, prior_notices: e.target.value }))} placeholder="Describe any prior communications, or enter 'None'" rows={2} data-testid="intake-prior-notices" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Violation Mapping */}
          {currentStep === 3 && (
            <div className="space-y-5" data-testid="step-3-violations">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Scale className="w-5 h-5 text-red-600" /> Violation Mapping
              </h2>
              <p className="text-sm text-gray-600">Select the categories that best describe your concerns. You may select multiple items.</p>

              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Procedural Concerns</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {PROCEDURAL_OPTIONS.map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded p-1.5">
                        <Checkbox checked={vProcedural.includes(opt)} onCheckedChange={() => toggleViolation('procedural', opt)} data-testid={`violation-procedural-${opt.slice(0,20).replace(/\s/g,'-')}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Constitutional / Rights-Based Concerns</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {RIGHTS_OPTIONS.map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded p-1.5">
                        <Checkbox checked={vRights.includes(opt)} onCheckedChange={() => toggleViolation('rights', opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Administrative / Policy Concerns</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {ADMIN_OPTIONS.map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded p-1.5">
                        <Checkbox checked={vAdmin.includes(opt)} onCheckedChange={() => toggleViolation('administrative', opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Additional Details</label>
                  <Textarea value={violations.details} onChange={e => setViolations(p => ({ ...p, details: e.target.value }))} placeholder="Provide any additional context about the concerns selected above..." rows={3} data-testid="violation-details" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Evidence Builder */}
          {currentStep === 4 && (
            <div className="space-y-5" data-testid="step-4-evidence">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FilePlus className="w-5 h-5 text-red-600" /> Evidence Builder
              </h2>
              <p className="text-sm text-gray-600">Upload supporting documents. Each file is auto-labeled as an exhibit.</p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-3">Documents, correspondence, receipts, photos, screenshots</p>
                <label className="cursor-pointer">
                  <Button variant="outline" disabled={uploading} asChild>
                    <span>
                      {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Choose File</>}
                    </span>
                  </Button>
                  <input type="file" className="hidden" onChange={handleUploadEvidence} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif" data-testid="evidence-file-input" />
                </label>
                <p className="text-xs text-gray-400 mt-2">Max 10MB per file</p>
              </div>

              {evidence.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700">Uploaded Evidence ({evidence.length})</h3>
                  {evidence.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border" data-testid={`evidence-item-${e.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge className="bg-red-100 text-red-700 flex-shrink-0">{e.exhibit_label}</Badge>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{e.filename}</p>
                          <p className="text-xs text-gray-500">{(e.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteEvidence(e.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {evidence.length === 0 && (
                <p className="text-sm text-gray-500 italic text-center py-4">No evidence uploaded yet. You can proceed without evidence, but supported claims are stronger.</p>
              )}
            </div>
          )}

          {/* STEP 5: Fee Schedule (Optional) */}
          {currentStep === 5 && (
            <div className="space-y-5" data-testid="step-5-fees">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileDown className="w-5 h-5 text-red-600" /> Fee / Damages Schedule
                <Badge variant="outline" className="ml-2 text-xs">Optional</Badge>
              </h2>
              <p className="text-sm text-gray-600">Optionally outline any claimed financial losses or costs related to this matter.</p>

              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={feeSchedule.enabled} onCheckedChange={checked => setFeeSchedule(p => ({ ...p, enabled: !!checked }))} data-testid="fee-toggle" />
                <span className="text-sm font-medium text-gray-700">Include a fee / damages schedule</span>
              </label>

              {feeSchedule.enabled && (
                <div className="space-y-4 border rounded-lg p-4">
                  {feeSchedule.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Description</label>
                        <Input value={item.description} onChange={e => {
                          const items = [...feeSchedule.items];
                          items[idx] = { ...items[idx], description: e.target.value };
                          setFeeSchedule(p => ({ ...p, items }));
                        }} placeholder="e.g., Lost wages" />
                      </div>
                      <div className="w-28">
                        <label className="text-xs text-gray-500">Amount ($)</label>
                        <Input type="number" value={item.amount} onChange={e => {
                          const items = [...feeSchedule.items];
                          items[idx] = { ...items[idx], amount: e.target.value };
                          setFeeSchedule(p => ({ ...p, items }));
                        }} placeholder="0.00" />
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 mb-0.5" onClick={() => setFeeSchedule(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setFeeSchedule(p => ({ ...p, items: [...p.items, { description: '', amount: '' }] }))}>
                    <Plus className="w-4 h-4 mr-1" /> Add Line Item
                  </Button>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">How Were Amounts Calculated?</label>
                    <Textarea value={feeSchedule.calculation_notes} onChange={e => setFeeSchedule(p => ({ ...p, calculation_notes: e.target.value }))} placeholder="Describe how you arrived at these amounts..." rows={2} />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-700">
                    <strong>Note:</strong> These amounts are user-provided estimates. They are not guaranteed or enforceable and are presented for informational purposes only.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 6: Destination */}
          {currentStep === 6 && (
            <div className="space-y-5" data-testid="step-6-destination">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-red-600" /> Destination Guidance
              </h2>
              <p className="text-sm text-gray-600">Identify where you plan to send this complaint. This is general guidance, not legal instruction.</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 space-y-2">
                <p className="font-semibold">Common recipients include:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Agency complaint or compliance office</li>
                  <li>Internal Affairs division</li>
                  <li>Inspector General</li>
                  <li>Oversight board or commission</li>
                  <li>Insurer (if applicable)</li>
                  <li>Court clerk (if filing with a court)</li>
                </ul>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Recipient Type</label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                    value={destination.recipient_type}
                    onChange={e => setDestination(p => ({ ...p, recipient_type: e.target.value }))}
                    data-testid="destination-type"
                  >
                    <option value="">Select a recipient type...</option>
                    {RECIPIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Recipient Name / Office</label>
                  <Input value={destination.recipient_name} onChange={e => setDestination(p => ({ ...p, recipient_name: e.target.value }))} placeholder="e.g., Office of the Inspector General" data-testid="destination-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Mailing Address</label>
                  <Textarea value={destination.address} onChange={e => setDestination(p => ({ ...p, address: e.target.value }))} placeholder="Full mailing address for the recipient" rows={2} data-testid="destination-address" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 7: Review & Generate */}
          {currentStep === 7 && (
            <div className="space-y-5" data-testid="step-7-generate">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" /> Review & Generate Complaint
              </h2>
              <p className="text-sm text-gray-600">Review all collected information below, then generate your formal complaint document.</p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-4 text-sm">
                <h3 className="font-semibold text-gray-800 border-b pb-2">Complaint Summary</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><span className="text-gray-500">Complainant:</span> <strong>{intake.complainant_name || '—'}</strong></div>
                  <div><span className="text-gray-500">Public Agent:</span> <strong>{intake.agent_name || '—'}</strong></div>
                  <div><span className="text-gray-500">Agency:</span> <strong>{intake.agency || '—'}</strong></div>
                  <div><span className="text-gray-500">Date:</span> <strong>{intake.incident_date || '—'}</strong></div>
                  <div><span className="text-gray-500">Location:</span> <strong>{intake.location || '—'}</strong></div>
                  <div><span className="text-gray-500">Evidence:</span> <strong>{evidence.length} file(s)</strong></div>
                </div>

                {intake.description && (
                  <div>
                    <span className="text-gray-500 block mb-1">Description of Conduct:</span>
                    <p className="text-gray-800 bg-white p-2 rounded border text-xs leading-relaxed">{intake.description.length > 200 ? intake.description.slice(0, 200) + '...' : intake.description}</p>
                  </div>
                )}

                <div>
                  <span className="text-gray-500">Concerns Selected:</span>{' '}
                  <strong>{[...vProcedural, ...vRights, ...vAdmin].length}</strong>
                  {vProcedural.length > 0 && <span className="ml-2 text-xs text-gray-500">({vProcedural.length} procedural)</span>}
                  {vRights.length > 0 && <span className="ml-1 text-xs text-gray-500">({vRights.length} rights)</span>}
                  {vAdmin.length > 0 && <span className="ml-1 text-xs text-gray-500">({vAdmin.length} admin)</span>}
                </div>

                {feeSchedule.enabled && feeSchedule.items.length > 0 && (
                  <div>
                    <span className="text-gray-500">Fee Schedule:</span>{' '}
                    <strong>{feeSchedule.items.length} item(s) — ${feeSchedule.items.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0).toFixed(2)} total</strong>
                  </div>
                )}

                {destination.recipient_name && (
                  <div>
                    <span className="text-gray-500">Destination:</span>{' '}
                    <strong>{destination.recipient_name}</strong>
                    {destination.recipient_type && <span className="text-xs text-gray-500 ml-1">({destination.recipient_type})</span>}
                  </div>
                )}
              </div>

              <div className="text-center py-4">
                {generatedDoc ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Complaint document generated successfully!</span>
                    </div>
                    <p className="text-sm text-gray-500">Proceed to the next step to download your document.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">All information has been collected. Click below to generate your formal complaint.</p>
                    <Button onClick={generateComplaint} disabled={generating} className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-base" data-testid="generate-complaint-btn">
                      {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Complaint (up to 2 min)...</> : <><FileText className="w-4 h-4 mr-2" /> Generate Formal Complaint</>}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 8: Download */}
          {currentStep === 8 && (
            <div className="space-y-5 text-center" data-testid="step-8-download">
              <h2 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
                <Download className="w-5 h-5 text-green-600" /> Your Complaint is Ready
              </h2>

              {generatedDoc ? (
                <div className="space-y-6 py-4">
                  <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-5 py-2 text-green-700 font-semibold">
                    <CheckCircle className="w-5 h-5" /> Document Generated Successfully
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 max-w-sm mx-auto">
                    <FileText className="w-12 h-12 mx-auto text-red-500 mb-3" />
                    <p className="font-semibold text-gray-900 mb-1">{generatedDoc.filename || 'Formal_Complaint.docx'}</p>
                    <p className="text-xs text-gray-500 mb-4">Word Document (.docx) — Print-ready with numbered paragraphs, exhibit list, and signature block</p>
                    <Button onClick={downloadDoc} className="bg-red-600 hover:bg-red-700 text-white w-full" data-testid="download-complaint-btn">
                      <Download className="w-4 h-4 mr-2" /> Download Complaint
                    </Button>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 max-w-md mx-auto text-left">
                    <p className="font-semibold mb-2">Next Steps:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Review the document carefully before submitting</li>
                      <li>Make any manual edits in the DOCX file as needed</li>
                      <li>Send via Certified Mail with Return Receipt for proof of delivery</li>
                      <li>Keep a copy for your records</li>
                      <li>Note the date of submission and follow up if no response</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
                  <p className="text-gray-700 mb-4">No document generated yet. Go back to Step 7 to generate your complaint.</p>
                  <Button onClick={() => setCurrentStep(7)} variant="outline">Go to Step 7</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={goBack} disabled={currentStep === 1} data-testid="wizard-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <span className="text-sm text-gray-500 hidden sm:block">{STEPS[currentStep - 1]?.label}</span>
        {currentStep < 8 && (
          <Button onClick={goNext} className="bg-red-600 hover:bg-red-700 text-white" data-testid="wizard-next-btn">
            {currentStep === 5 && !feeSchedule.enabled ? 'Next (Skip Fees)' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        {currentStep === 8 && (
          <Button variant="outline" onClick={() => { saveProgress(); setActiveComplaint(null); }} data-testid="wizard-done-btn">
            Done
          </Button>
        )}
      </div>
    </div>
  );
}
